import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Package, Truck, Home as HomeIcon, ArrowLeft, Copy } from "lucide-react";
import api from "@/lib/api";
import { TRACKING } from "@/constants/testIds";
import { toast } from "sonner";

const STAGE_META = [
  { key: "confirmed", label: "Order confirmed", desc: "We received your offering", Icon: CheckCircle2 },
  { key: "packed",    label: "Packed with care",    desc: "Wrapped with sacred cotton",  Icon: Package },
  { key: "shipped",   label: "On its way",           desc: "Handed to the courier",        Icon: Truck },
  { key: "delivered", label: "Delivered",             desc: "Blessings arrive at your door", Icon: HomeIcon },
];

const OrderTracking = () => {
  const { sessionId } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/me/orders/${sessionId}`)
      .then((r) => setOrder(r.data))
      .catch((e) => setErr(e.response?.status === 404 ? "Order not found" : "Sign in to view this order"));
  }, [sessionId]);

  if (err) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h1 className="font-serif text-3xl">{err}</h1>
      <Link to="/account" className="inline-flex items-center gap-2 mt-6 text-primary">
        <ArrowLeft className="w-4 h-4" /> Back to account
      </Link>
    </div>
  );
  if (!order) return <div className="max-w-6xl mx-auto px-6 py-24 text-muted-foreground">Loading…</div>;

  const currentIdx = order.fulfillment.stage_index;

  const copyTracking = async () => {
    try {
      await navigator.clipboard.writeText(order.fulfillment.tracking_number);
      toast.success("Tracking number copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div data-testid={TRACKING.page} className="max-w-4xl mx-auto px-6 sm:px-10 py-16">
      <Link to="/account" className="inline-flex items-center gap-2 text-xs uppercase tracking-label text-muted-foreground hover:text-primary">
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back to account
      </Link>

      <p className="text-xs uppercase tracking-label text-secondary mt-8">Order tracking</p>
      <h1 className="display-serif text-4xl md:text-5xl mt-3">Your sacred bundle is on its way.</h1>

      {/* Tracking header */}
      <div className="mt-10 grid sm:grid-cols-3 gap-6">
        <div className="editorial-card p-5">
          <p className="text-[10px] uppercase tracking-label text-muted-foreground">Tracking number</p>
          <div className="mt-2 flex items-center justify-between gap-2">
            <span data-testid={TRACKING.trackingNumber} className="font-serif text-xl">{order.fulfillment.tracking_number}</span>
            <button onClick={copyTracking} className="p-1 hover:text-primary" aria-label="copy"><Copy className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
          </div>
        </div>
        <div className="editorial-card p-5">
          <p className="text-[10px] uppercase tracking-label text-muted-foreground">Estimated delivery</p>
          <p data-testid={TRACKING.estimatedDelivery} className="font-serif text-xl mt-2">
            {order.fulfillment.estimated_delivery}
          </p>
        </div>
        <div className="editorial-card p-5">
          <p className="text-[10px] uppercase tracking-label text-muted-foreground">Order total</p>
          <p className="font-serif text-xl mt-2">${(order.amount || 0).toFixed(2)}</p>
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-12 editorial-card p-8">
        <div className="space-y-8 relative">
          <div className="absolute left-5 top-3 bottom-3 w-px bg-border" aria-hidden="true" />
          {STAGE_META.map((stage, idx) => {
            const done = idx <= currentIdx;
            const active = idx === currentIdx;
            const Icon = stage.Icon;
            return (
              <div key={stage.key} data-testid={TRACKING.stage(idx)} className="relative flex items-start gap-5">
                <div className={`relative z-10 w-11 h-11 flex items-center justify-center border ${done ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground"}`}>
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="pt-1.5">
                  <p className={`text-[11px] uppercase tracking-label ${active ? "text-primary" : done ? "text-foreground" : "text-muted-foreground"}`}>
                    Step {idx + 1} · {stage.label}
                  </p>
                  <p className={`font-serif text-xl mt-1 ${done ? "" : "text-muted-foreground"}`}>{stage.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Items */}
      <div className="mt-10">
        <p className="text-[10px] uppercase tracking-label text-muted-foreground mb-4">Items in this order</p>
        <div className="editorial-card divide-y divide-border">
          {(order.items || []).map((it) => (
            <div key={it.sku} className="flex items-center justify-between p-5">
              <div>
                <p className="text-sm">{it.name}</p>
                <p className="text-[11px] uppercase tracking-label text-muted-foreground mt-1">Qty {it.qty}</p>
              </div>
              <p className="font-serif text-lg">${(it.price * it.qty).toFixed(2)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
